<script>
  import { createEventDispatcher } from "svelte";
  import { Icon, BugAnt, Play, ArrowPath, CodeBracket } from "svelte-hero-icons";
  import { execution, features, filetree, jobs, liveStore } from "../store";
  import { persisted } from "$lib/utils/persisted-stores";
  import DropdownMenu from "$lib/Elements/DropdownMenu.svelte";
  import Button from "$lib/Elements/Button.svelte";
  import SelectInput from "$lib/Inputs/SelectInput.svelte";
  import BuildInput from "$lib/Inputs/BuildInput.svelte";
  import KeyValueConfigurator from "$lib/Inputs/KeyValueConfigurator.svelte";
  import CheckboxInput from "$lib/Inputs/CheckboxInput.svelte";
  import SidecarFilterInput from "$lib/Inputs/SidecarFilterInput.svelte";
  import Modal from "$lib/Elements/Modal.svelte";
  import CopyText from "$lib/Elements/CopyText.svelte";
  import CopyButton from "$lib/Elements/CopyButton.svelte";
  import { deferred } from "$lib/utils/deferred";
  import { workflowSnippet } from "$utils/webdriver-workflow-snippets";

  export let apps;
  export let devices;
  export let regions;
  export let availableSidecars;
  export let availableEntrypoints;
  export let projectId = null;
  export let selectedApp = null;
  export let selectedBuild = null;
  export let selectedDevice = null;
  export let selectedRegion = null;
  export let selectedStaticTeleport = false;
  export let selectedSidecar = null;
  export let selectedEntrypoint = null;
  export let godMode = false;
  export let ignoreWarmupErrors = false;
  export let skipHealthchecks = false;

  const dispatch = createEventDispatcher();
  const { mainScriptId } = filetree;

  $: executionMode = execution.mode;
  $: showRootRequestOptions = features.showRootRequestOptions;
  $: sidecarRead = features.sidecarRead;
  $: isRunning = $executionMode == "in_progress";
  $: currentJob = jobs.currentJob;

  $: selectedEntrypoint = $mainScriptId || null;
  const isReady = deferred();
  const rememberMyChoices = persisted("remember_my_choices", false);
  const persistedGodMode = persisted("trace_debug_god_mode", false);
  const persistedIgnoreWarmupErrors = persisted("trace_debug_ignore_warmup_errors", false);
  const persistedSkipHealthchecks = persisted("trace_debug_skip_healthchecks", false);
  const onlyEntrypoints = persisted("trace_debug_only_entrypoints", true);
  const persistedSerialNumbersMode = persisted("trace_debug_serial_numbers_mode", "expand");

  $: persistedDeviceStore =
    $rememberMyChoices && projectId != null
      ? persisted(`${projectId}-trace_debug_device`, null)
      : null;
  $: persistedSelectedSidecarsStore =
    $rememberMyChoices && projectId != null
      ? persisted(`${projectId}-trace_debug_sidecars`, [])
      : null;

  if ($rememberMyChoices) {
    godMode = $persistedGodMode;
    ignoreWarmupErrors = $persistedIgnoreWarmupErrors;
    skipHealthchecks = $persistedSkipHealthchecks;
  }

  let defaultText = "Trace";
  let textDebugButton = defaultText;
  let lastSelection = null;
  let isBuildInputOpen = false;
  let isCliModalOpen = false;
  let cliModalTab = "cli";
  let webdriverRunMode = "background";
  let webdriverSnippetHtml = null;
  let webdriverSnippetHtmlFor = null;
  let webdriverHighlightRequest = 0;
  let webdriverWorkflowId = null;
  let webdriverKeyValues = [];

  function getDeviceName(id) {
    return devices.find((device) => device.value == id)?.label ?? "n/a";
  }

  function debug() {
    lastSelection = "debug";
    textDebugButton = `Debug on ${getDeviceName(selectedDevice)}`;
    execution.triggerDebug(
      selectedApp,
      selectedDevice,
      selectedRegion,
      selectedSidecar,
      selectedEntrypoint,
      selectedBuild,
      godMode,
      ignoreWarmupErrors,
      skipHealthchecks,
      $persistedSerialNumbersMode,
      isStaticTeleportActive,
    );
  }

  function trace() {
    lastSelection = "trace";
    textDebugButton = `Trace on ${getDeviceName(selectedDevice)}`;
    execution.triggerTrace(
      selectedApp,
      selectedDevice,
      selectedRegion,
      selectedSidecar,
      selectedEntrypoint,
      selectedBuild,
      godMode,
      ignoreWarmupErrors,
      skipHealthchecks,
      $persistedSerialNumbersMode,
      isStaticTeleportActive,
    );
  }

  function onButtonClick(_e, { toggleDropdown }) {
    if (isRunning) {
      return execution.cancelJob($currentJob.id);
    }

    if (lastSelection === null) {
      toggleDropdown();
    }

    switch (lastSelection) {
      case "trace":
        return trace();
      case "debug":
        return debug();
    }
  }

  function resetButton() {
    textDebugButton = defaultText;
    lastSelection = null;
  }

  function isBuildAllowed(appId) {
    return apps.find((app) => app.value == appId)?.buildsAllowed ?? false;
  }

  async function onOpen() {
    dispatch("open", {});
    dispatch("updateEntrypoints", { onlyEntrypoints: $onlyEntrypoints });
    await isReady.promise;

    if ($rememberMyChoices) {
      if (
        persistedDeviceStore &&
        $persistedDeviceStore &&
        devices?.some((d) => d.value === $persistedDeviceStore)
      ) {
        selectedDevice = $persistedDeviceStore;
      }
      if (persistedSelectedSidecarsStore && Array.isArray($persistedSelectedSidecarsStore)) {
        selectedSidecar = [...$persistedSelectedSidecarsStore];
      }
    }
  }

  function onAppChange() {
    selectedBuild = null;
  }

  async function onDeviceChange() {
    dispatch("deviceChange", {});
  }

  function onSidecarFilterChange(e) {
    selectedSidecar = e.detail.selectedSidecars;
    if (persistedSelectedSidecarsStore && $rememberMyChoices) {
      persistedSelectedSidecarsStore.set(e.detail.selectedSidecars);
    }
  }

  async function onOnlyEntrypointsChange() {
    dispatch("updateEntrypoints", { onlyEntrypoints: $onlyEntrypoints });
  }

  $: if (apps !== null) isReady.resolve();
  $: isSelectionIncomplete = !selectedApp || !selectedDevice;
  $: isSelectionIncomplete && resetButton();

  $: isDefaultRegion = !selectedRegion || selectedRegion === "new_york_usa";
  $: isStaticTeleportActive = !isDefaultRegion && selectedStaticTeleport;

  function generateCliCommand() {
    if (!selectedApp || !selectedDevice) {
      return "# Please select both an app and device first\n# Use the form above to configure targets";
    }

    if (!projectId) {
      return "# Project ID not available\n# Please ensure you have a valid project context";
    }

    let command = `tvlabs run --project ${projectId} --app ${selectedApp} --device ${selectedDevice}`;

    if (selectedBuild) {
      command += ` --build ${selectedBuild}`;
    }

    if (selectedEntrypoint) {
      command += ` --entrypoint ${selectedEntrypoint}`;
    }

    if (selectedRegion && selectedRegion !== "new_york_usa") {
      command += ` --region ${selectedRegion}`;
    }

    return command;
  }

  async function showCliModal() {
    isCliModalOpen = true;

    const { key_values } = await $liveStore.pushEvent("request_webdriver_key_values", {});
    webdriverKeyValues = key_values
      .filter(({ secret }) => !secret)
      .map(({ name, value }) => ({ name, value }));
  }

  // Libraries can't be run on their own. The tab starts on the entrypoint picked
  // for Debug/Trace, which is the main script unless changed, and falls back to
  // the project's first workflow when neither is set.
  $: webdriverWorkflows = ($availableEntrypoints || []).filter(({ isLibrary }) => !isLibrary);

  $: if (!webdriverWorkflows.some(({ value }) => value === webdriverWorkflowId)) {
    webdriverWorkflowId =
      webdriverWorkflows.find(({ value }) => value === selectedEntrypoint)?.value ??
      webdriverWorkflows[0]?.value ??
      null;
  }

  $: webdriverSnippet = webdriverWorkflowId
    ? workflowSnippet({
        workflowId: webdriverWorkflowId,
        keyValues: webdriverKeyValues,
        wait: webdriverRunMode === "foreground",
      })
    : "// This project has no workflow to run yet";

  // Highlighted by the LiveView with the app's own code viewer. Only the reply
  // to the latest request is kept, so a slow reply can't show an older snippet.
  $: if (isCliModalOpen && cliModalTab === "webdriver") {
    highlightWebdriverSnippet(webdriverSnippet);
  }

  async function highlightWebdriverSnippet(code) {
    const request = ++webdriverHighlightRequest;
    const { html } = await $liveStore.pushEvent("highlight_webdriver_snippet", { code });

    if (request === webdriverHighlightRequest) {
      webdriverSnippetHtml = html;
      webdriverSnippetHtmlFor = code;
    }
  }
</script>

<DropdownMenu
  buttonText={isRunning ? "Cancel Job" : textDebugButton}
  buttonClass={isRunning ? "opacity-85" : ""}
  {onOpen}
  onClick={onButtonClick}
  hasSeperateDropdown
  disableClickOutside={isBuildInputOpen}
>
  <div slot="prefix" class="flex items-center">
    {#if isRunning}
      <Icon src={ArrowPath} class="mr-1 h-4 w-4 animate-spin" />
    {:else}
      <Icon src={lastSelection == "debug" ? BugAnt : Play} class="w-4 h-4 mr-1" />
    {/if}
  </div>

  <div slot="content" let:close>
    <h3 class="text-md font-semibold text-content mb-1">Debug Automation</h3>
    <p class="text-sm text-content-muted mb-3">Select an App and Device to debug</p>

    <div class="mb-2">
      <SelectInput
        bind:value={selectedApp}
        on:change={onAppChange}
        optional
        label="Select an App"
        options={apps}
        variant="wide"
        shouldPersist={rememberMyChoices}
        persistedName={`${projectId}-trace_debug_app`}
      />
    </div>

    <div class="mb-2">
      <BuildInput
        {selectedApp}
        isReadonly={!isBuildAllowed(selectedApp)}
        label="Select a Build"
        bind:isOpen={isBuildInputOpen}
        bind:value={selectedBuild}
        {projectId}
        shouldPersist={rememberMyChoices}
        persistedName={`${projectId}-trace_debug_build`}
      />
    </div>

    <div class="mb-2">
      <SelectInput
        bind:value={selectedDevice}
        on:change={onDeviceChange}
        optional
        label="Select a Device"
        options={devices}
        variant="wide"
        {projectId}
        shouldPersist={rememberMyChoices}
        persistedName={`${projectId}-trace_debug_device`}
      />
    </div>

    {#if $showRootRequestOptions && $availableSidecars.length > 0}
      <div class="flex items-center w-full pl-4 pr-6 mb-2">
        <SidecarFilterInput
          sidecars={$availableSidecars}
          selectedSidecars={selectedSidecar}
          showSidecarLink={$sidecarRead}
          on:change={onSidecarFilterChange}
        />
      </div>
    {/if}

    <div class="mb-4">
      <SelectInput
        bind:value={selectedRegion}
        optional
        label="Teleport Region"
        trim
        options={regions}
        variant="wide"
        defaultValue="new_york_usa"
        {projectId}
        shouldPersist={rememberMyChoices}
        persistedName={`${projectId}-trace_debug_teleport_region`}
      />

      {#if !isDefaultRegion}
        <div class="mt-2 ml-4 text-xs">
          <CheckboxInput
            value={selectedStaticTeleport}
            on:input={(e) => (selectedStaticTeleport = e.detail)}
            label="Require local app store credentials"
            persistedName={`${projectId}-require_local_app_store_credentials`}
          />
        </div>
      {/if}
    </div>

    <div class="mb-4">
      <SelectInput
        bind:value={selectedEntrypoint}
        optional
        label="Entrypoint"
        options={$availableEntrypoints}
        variant="wide"
        {projectId}
        shouldPersist={rememberMyChoices}
        persistedName={`${projectId}-trace_debug_entrypoint`}
      />
      <div class="mt-2 text-xs">
        <CheckboxInput
          value={!$onlyEntrypoints}
          on:input={(e) => {
            onlyEntrypoints.set(!e.detail);
            onOnlyEntrypointsChange();
          }}
          label="Include Library Scripts"
        />
      </div>
    </div>

    <div class="mb-2">
      <KeyValueConfigurator
        label="Override Key-Value Pairs"
        bind:isOpen={isBuildInputOpen}
        bind:value={selectedBuild}
        {projectId}
        shouldPersist={rememberMyChoices}
        persistedName={`${projectId}-key_value_configuration`}
      />
    </div>

    {#if $showRootRequestOptions}
      <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
        <p class="text-xs mb-2 text-yellow-700 font-medium">Developer options:</p>
        <div class="space-y-2 text-xs">
          <CheckboxInput
            value={godMode}
            on:input={(e) => {
              godMode = e.detail;
              if (persistedGodMode) {
                persistedGodMode.set(e.detail);
              }
            }}
            label="Run in God Mode"
          />
          <CheckboxInput
            value={ignoreWarmupErrors}
            on:input={(e) => {
              ignoreWarmupErrors = e.detail;
              if (persistedIgnoreWarmupErrors) {
                persistedIgnoreWarmupErrors.set(e.detail);
              }
            }}
            label="Ignore Warmup Errors"
          />
          <CheckboxInput
            value={skipHealthchecks}
            on:input={(e) => {
              skipHealthchecks = e.detail;
              if (persistedSkipHealthchecks) {
                persistedSkipHealthchecks.set(e.detail);
              }
            }}
            label="Skip Healthchecks"
          />
          <CheckboxInput
            value={$persistedSerialNumbersMode === "filter"}
            on:input={(e) => persistedSerialNumbersMode.set(e.detail ? "filter" : "expand")}
            label="Single job (run on any selected sidecar)"
          />
        </div>
      </div>
    {/if}

    <div class="mb-4 text-xs">
      <CheckboxInput
        value={$rememberMyChoices}
        on:input={(e) => rememberMyChoices.update(() => e.detail)}
        label="Remember My Choices"
      />
    </div>

    {#if isSelectionIncomplete}
      <p class="text-red-500 text-xs mb-4">
        Please select both an app and a device on the "Settings" page.
      </p>
    {/if}

    {#if isRunning}
      <p class="text-red-500 text-xs mb-4">Run in progress.</p>
    {/if}

    <div class="flex justify-end space-x-2">
      <Button variant="secondary" on:click={close}>Cancel</Button>

      <Button
        variant="secondary"
        icon={CodeBracket}
        on:click={() => {
          showCliModal();
        }}
      >
        <span class="whitespace-nowrap">CLI & WebDriver</span>
      </Button>

      <Button
        disabled={isSelectionIncomplete || isRunning}
        icon={BugAnt}
        on:click={() => {
          debug();
          close();
        }}
      >
        Debug
      </Button>

      <Button
        disabled={isSelectionIncomplete || isRunning}
        icon={Play}
        on:click={() => {
          trace();
          close();
        }}
      >
        Trace
      </Button>
    </div>
  </div>
</DropdownMenu>

<Modal
  bind:open={isCliModalOpen}
  title="Run Outside the Editor"
  maxWidth="48rem"
  on:close={() => (isCliModalOpen = false)}
>
  <div class="flex gap-4 border-b border-line-strong mb-4 text-sm">
    {#each [{ value: "cli", label: "CLI" }, { value: "webdriver", label: "WebDriver" }] as tab}
      <button
        type="button"
        class="pb-2 -mb-px border-b-2 {cliModalTab === tab.value
          ? 'border-action text-content font-medium'
          : 'border-transparent text-content-subtle hover:text-content'}"
        on:click={() => (cliModalTab = tab.value)}
      >
        {tab.label}
      </button>
    {/each}
  </div>

  {#if cliModalTab === "cli"}
    <div class="space-y-4">
      <p class="text-sm text-content-muted mb-4">
        Copy this command to run your automation from the CLI:
      </p>

      <div class="bg-surface-sunken p-4 rounded-lg border border-line-strong">
        <CopyText
          text={generateCliCommand()}
          showTooltip={false}
          class="font-mono text-sm text-content whitespace-pre-wrap"
        />
      </div>

      <div class="text-xs text-content-subtle space-y-2">
        {#if projectId}
          <p>
            <strong>Project:</strong> <code>{projectId}</code>
          </p>
        {/if}
        {#if !selectedApp || !selectedDevice}
          <p class="text-orange-600">
            <strong>Tip:</strong> Use the form above to select your target app and device first.
          </p>
        {/if}
        {#if !projectId}
          <p class="text-red-600">
            <strong>Error:</strong> Project ID not available. Please ensure you have a valid project context.
          </p>
        {/if}
      </div>

      <div class="pt-4 border-t border-line-strong">
        <h4 class="text-sm font-semibold text-content mb-2">Available Parameters:</h4>
        <div class="text-xs text-content-muted space-y-1">
          <div><code>--project</code> - Your automation project ID (required)</div>
          <div><code>--app</code> - Target application ID (required)</div>
          <div><code>--device</code> - Target device ID (required)</div>
          <div><code>--build</code> - Specific build version (optional)</div>
          <div><code>--entrypoint</code> - Specific workflow to run (optional)</div>
          <div><code>--region</code> - Teleport region (optional, defaults to new_york_usa)</div>
        </div>
      </div>
    </div>
  {:else}
    <div class="space-y-4">
      <p class="text-sm text-content-muted">
        Run a workflow from inside one of your WebDriver sessions:
      </p>

      <SelectInput
        bind:value={webdriverWorkflowId}
        label="Workflow"
        options={webdriverWorkflows}
        variant="wide"
      />

      <div class="space-y-2">
        <div class="flex gap-2">
          {#each [{ value: "background", label: "Run in Background" }, { value: "foreground", label: "Run in Foreground" }] as mode}
            <button
              type="button"
              class="px-2 py-1 rounded text-xs {webdriverRunMode === mode.value
                ? 'bg-action text-action-content'
                : 'bg-surface-muted text-content hover:bg-surface-hover'}"
              on:click={() => (webdriverRunMode = mode.value)}
            >
              {mode.label}
            </button>
          {/each}
        </div>

        {#if webdriverRunMode === "background"}
          <p class="text-xs text-content-muted">
            Long workflows should run in the background. The call answers at once with a
            <code>runId</code>. Poll <code>tvlabs: getWorkflowRun</code> for the run's status, and
            <code>tvlabs: stopWorkflowRun</code> stops it. A run times out after 5 minutes unless
            you pass <code>timeout</code> in milliseconds, up to 30 minutes.
          </p>
        {:else}
          <p class="text-xs text-content-muted">
            Short workflows can run in a single request. When executing a workflow in the
            foreground, the call answers when the run finishes. The time limit on this type of
            request is 10 minutes. Raise your WebDriver client's request timeout to match.
          </p>
        {/if}
      </div>

      <div class="relative">
        <!-- Plain until the highlighted copy of this exact snippet arrives. -->
        {#if webdriverSnippetHtml && webdriverSnippetHtmlFor === webdriverSnippet}
          <!-- eslint-disable-next-line svelte/no-at-html-tags -->
          {@html webdriverSnippetHtml}
        {:else}
          <pre
            class="p-4 bg-surface border border-line rounded-lg text-xs leading-relaxed text-content overflow-x-auto">{webdriverSnippet}</pre>
        {/if}
        <CopyButton
          text={webdriverSnippet}
          label="Copy snippet"
          class="absolute top-3 right-3 text-content-subtle"
        />
      </div>

      <div class="text-xs text-content-muted space-y-2">
        <p>
          A workflow result that is a failure will still respond 200 at the HTTP level, but will
          include a <code>failure</code> key with the failure reason. Only a run that cannot start answers
          with a WebDriver error.
        </p>
      </div>
    </div>
  {/if}
</Modal>
